import React from 'react';
import styled from 'styled-components';
import { styles } from '../../../../styles/themes/style';
import { campaignMobile } from '../../utils/campaignBreakpoints';
import {
  ICampaign, ICampaignLead, ISequenceStep, CampaignLeadStatus,
  DEFAULT_SEQUENCE, SequenceStepType, TERMINAL_LEAD_STATUSES, STATUS_COLORS, LEAD_STATUS_LABELS, sequenceExpectsReply,
  SuppressionType,
} from '../../../../interfaces/models/campaign';
import { IProxyAccountDetails } from '../../../../services/apis/campaigns/useCampaignsRequest';
import Column from '../../../../components/Column';
import Row from '../../../../components/Row';
import SvgFragment from '../../../../fragments/SvgFragment';
import VerticalStepper, { IVerticalStep } from '../../../../components/VerticalStepper';
import { classicLinkedInProfileUrl } from '../../utils/salesNavListLeads';
import { formatStepDelay } from '../../../../utils/campaignSequenceSummary';

// ─── Step + status metadata (mirrors CampaignSequenceBuilder) ─────────────────

export const STEP_META: Record<SequenceStepType, { label: string; icon: string; color: string; bg: string; hint: string }> = {
  connection_request: { label: 'Connection Request', icon: 'stepConnect', color: '#6366f1', bg: '#eef2ff', hint: 'LinkedIn connection request sent' },
  message:            { label: 'Direct Message',     icon: 'stepMessage', color: '#16a34a', bg: '#dcfce7', hint: 'Direct message sent' },
  sn_message:         { label: 'InMail / Open Profile', icon: 'stepMessage', color: '#0da2e7', bg: '#e0f5fc', hint: 'Open Profile or InMail message sent' },
  profile_visit:      { label: 'Profile Visit',       icon: 'stepVisit',   color: '#7a6bb0', bg: '#f3f0fa', hint: 'Viewed the prospect’s profile' },
  post_like:          { label: 'Post Like',           icon: 'stepLike',    color: '#e11d48', bg: '#ffe4e6', hint: "Liked the prospect's post" },
  post_comment:       { label: 'Post Comment',        icon: 'stepComment', color: '#ea580c', bg: '#ffedd5', hint: "Commented on the prospect's post" },
  email:              { label: 'Email',               icon: 'stepEmail',   color: '#0891b2', bg: '#cffafe', hint: 'Email sent from the sender’s mailbox' },
};

const STATUS_LABELS = LEAD_STATUS_LABELS;
const STATUS_META: Partial<Record<string, { color: string; bg: string; label: string }>> = Object.fromEntries(
  (Object.keys(STATUS_LABELS) as CampaignLeadStatus[]).map(s => [s, {
    color: STATUS_COLORS[s].color, bg: STATUS_COLORS[s].bg, label: STATUS_LABELS[s],
  }]),
);

const SOURCE_LABEL: Record<string, string> = {
  leadsearch: 'LeadSearch',
  contacts:   'LD Contact',
  csv:        'CSV',
};

const FAILURE_REASON_LABELS: Record<string, string> = {
  not_connected: 'Not connected on LinkedIn',
  invite_not_accepted: 'Connection invite was not accepted in time',
  manual: 'Marked as failed manually',
  profile_lookup_failed: 'LinkedIn profile lookup failed',
  profile_posts_fetch_failed: 'Could not fetch profile posts',
  profile_visit_failed: 'LinkedIn rejected the profile visit',
  post_like_failed: 'LinkedIn rejected the like action',
  post_comment_failed: 'LinkedIn rejected the comment',
  connection_request_failed: 'LinkedIn rejected the invitation',
  linkedin_custom_invite_limit: 'LinkedIn personalized invite limit reached',
  note_quota_pause_noted: 'Monthly connection-note quota reached',
  note_quota_pause_campaign: 'Campaign paused — note quota reached',
  weekly_limit_reached: 'Weekly connection request limit reached',
  daily_limit_reached: 'Daily activity limit reached',
  message_send_failed: 'LinkedIn rejected the message',
  unknown: 'Unknown error',
};

function formatFailureReason(reason?: string): string {
  if (!reason) return 'Campaign step failed';
  return FAILURE_REASON_LABELS[reason] ?? reason.replace(/_/g, ' ');
}

const OUTCOME_ACTIVITY: Partial<Record<CampaignLeadStatus, { label: string; color: string; bg: string }>> = {
  failed:      { label: 'Failed',      color: STATUS_COLORS.failed.color,      bg: STATUS_COLORS.failed.bg },
  converted:   { label: 'Converted',   color: STATUS_COLORS.converted.color,   bg: STATUS_COLORS.converted.bg },
  closed_lost: { label: 'Closed lost', color: STATUS_COLORS.closed_lost.color, bg: STATUS_COLORS.closed_lost.bg },
  replied:     { label: 'Replied',     color: STATUS_COLORS.replied.color,     bg: STATUS_COLORS.replied.bg },
  suppressed:  { label: 'Suppressed',  color: STATUS_COLORS.suppressed.color,  bg: STATUS_COLORS.suppressed.bg },
};

/** "domain acme.com", "company name Acme (acme.com)", "LinkedIn profile jane-doe"… */
export function describeLeadSuppression(lead: ICampaignLead): string {
  const s = lead.suppression;
  if (!s?.value) return 'an entry on the suppression list';
  if (s.matchedBy === 'company_name') return `the company behind ${s.value}`;
  if (s.type === 'profile') return `LinkedIn profile ${s.value}`;
  return `${s.type} ${s.value}`;
}

/**
 * Left out by the campaign's "Skip people I'm already connected with" setting —
 * stored as 'suppressed' (final, never 'failed') but not on any suppression list.
 */
export function isExcludedAsConnected(lead: ICampaignLead): boolean {
  return lead.status === 'suppressed' && lead.suppression?.matchedBy === 'first_degree';
}

const EXCLUDED_AS_CONNECTED_TEXT = 'Already connected with you — skipped by “Skip people I’m already connected with”. Stays in the campaign, won’t be contacted';

/** One line for a tooltip: what matched, on which list, and the reason it was added. */
export function leadSuppressionTitle(lead: ICampaignLead): string {
  if (isExcludedAsConnected(lead)) return EXCLUDED_AS_CONNECTED_TEXT;
  const s = lead.suppression;
  const where = s?.listName ? `the suppression list “${s.listName}”` : 'the suppression list';
  const what = describeLeadSuppression(lead);
  const line = s?.value ? `${what.charAt(0).toUpperCase()}${what.slice(1)} is on ${where}` : `On ${where}`;
  return s?.note ? `${line}. Reason: ${s.note}` : line;
}

function outcomeActivityText(lead: ICampaignLead): string {
  if (lead.status === 'failed') return `Failed — ${formatFailureReason(lead.failureReason)}`;
  if (isExcludedAsConnected(lead)) return EXCLUDED_AS_CONNECTED_TEXT;
  if (lead.status === 'suppressed') return `Suppressed — ${describeLeadSuppression(lead)} is on the suppression list`;
  return OUTCOME_ACTIVITY[lead.status]?.label ?? STATUS_LABELS[lead.status];
}

function outcomeActivityTime(lead: ICampaignLead): string | null {
  return lead.messageSentAt || lead.lastContactedAt || lead.enrolledAt || null;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function fmt(d?: string | null) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}
function fmtTime(d?: string | null) {
  if (!d) return '';
  return new Date(d).toLocaleString(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

function renderThePostActivity(verb: 'Liked' | 'Commented', postUrl?: string) {
  if (!postUrl) return `${verb} the post`;
  return (
    <>
      {verb}{' '}
      <ActivityPostLink href={postUrl} target="_blank" rel="noopener noreferrer">the post</ActivityPostLink>
    </>
  );
}
function initials(name: string) {
  return name.split(/[\s\-]+/).map(w => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase() || '?';
}

// ─── Component ────────────────────────────────────────────────────────────────

interface Props {
  lead: ICampaignLead;
  campaign: ICampaign;
  proxyAccounts?: Record<string, IProxyAccountDetails>;
  onClose: () => void;
  /** Opens the add-to-list modal; without it the suppression block is not shown. */
  onSuppress?: (type: SuppressionType, value: string) => void;
}

/**
 * What this lead can be put on the suppression list as: the person, their company's
 * domain (from the company website, else a work address) and their address.
 */
function suppressionOptions(lead: ICampaignLead): { type: SuppressionType; value: string; label: string }[] {
  const options: { type: SuppressionType; value: string; label: string }[] = [];

  const profile = lead.publicIdentifier || lead.linkedinId || lead.salesNavProviderId || lead.providerId || '';
  if (profile) options.push({ type: 'profile', value: profile, label: 'This person' });

  const email = (lead.email || '').trim().toLowerCase();
  const emailDomain = email.includes('@') ? email.slice(email.lastIndexOf('@') + 1) : '';
  // A personal address says nothing about the employer.
  const domain = lead.companyDomain || (lead.emailType !== 'private' ? emailDomain : '');
  if (domain) options.push({ type: 'domain', value: domain, label: `Everyone at ${domain}` });

  if (email) options.push({ type: 'email', value: email, label: email });
  return options;
}

const CampaignLeadSidebar: React.FC<Props> = ({ lead, campaign, proxyAccounts, onClose, onSuppress }) => {
  const sequence: ISequenceStep[] = campaign.sequence?.length ? campaign.sequence : DEFAULT_SEQUENCE;
  // An email reply lands the lead in 'replied' just like a LinkedIn one, so the
  // outcome copy has to account for both channels.
  const hasMessageStep = sequenceExpectsReply(campaign.sequence);
  const stepIdx   = lead.currentStepIndex ?? 0;
  const isTerminal = TERMINAL_LEAD_STATUSES.includes(lead.status);
  const isFailed = lead.status === 'failed';
  // Taken out mid-sequence: progress stays where it stopped, like neither outcome.
  const isSuppressed = lead.status === 'suppressed';
  const isPositiveTerminal = isTerminal && !isFailed && !isSuppressed;
  const statusMeta = STATUS_META[lead.status] ?? STATUS_META.pending!;

  // Proxy: which LinkedIn account drives this lead's whole flow. The account is
  // assigned on the first action, so pending leads have none yet. If the assigned
  // account is no longer in the pool (disconnected), flag it — its remaining
  // steps will fail until the lead is reassigned.
  const isProxy     = campaign.executionMethod === 'proxy';
  const acctId      = lead.unipileAccountId;
  const acct        = acctId ? proxyAccounts?.[acctId] : undefined;
  const acctMissing = !!acctId && !!proxyAccounts && !acct;
  const acctName    = acct?.name || (acctId ? `Account ${acctId.slice(0, 8)}…` : '');

  const [imgFailed, setImgFailed] = React.useState(false);
  const showImg = !!lead.imageUrl && !imgFailed;
  const profileUrl = classicLinkedInProfileUrl(lead);

  return (
    <Wrap>
      {/* ── Top bar ─────────────────────────────────────── */}
      <TopBar>
        <TopTitle>Lead details</TopTitle>
        <CloseBtn type="button" onClick={onClose}>
          <SvgFragment type="close" />
        </CloseBtn>
      </TopBar>

      <Body>
        {/* ── Profile ──────────────────────────────────── */}
        <ProfileBlock>
          <AvatarWrap $hasImage={showImg}>
            {showImg
              ? <img src={lead.imageUrl} alt="" onError={() => setImgFailed(true)} style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
              : initials(lead.name || lead.publicIdentifier || '?')}
          </AvatarWrap>
          <Column gap="4px" style={{ flex: 1, minWidth: 0 }}>
            <ProfileName>{lead.name || lead.publicIdentifier || '—'}</ProfileName>
            {lead.title   && <ProfileSub>{lead.title}</ProfileSub>}
            {lead.company && <ProfileSub $bold>{lead.company}</ProfileSub>}
            {lead.location && <ProfileSub><LocationIcon><SvgFragment type="mapPin" /></LocationIcon>{lead.location}</ProfileSub>}
          </Column>
        </ProfileBlock>

        {profileUrl && (
          <LinkedInBtn href={profileUrl} target="_blank" rel="noopener noreferrer">
            <SvgFragment type="linkedin" />
            View LinkedIn profile
          </LinkedInBtn>
        )}

        {/* Already suppressed: "Suppressed by" below says why, there is nothing to add. */}
        {onSuppress && !isSuppressed && (() => {
          const options = suppressionOptions(lead);
          if (!options.length) return null;
          return (
            <SuppressBlock>
              <BlockLabel>Add to suppression list</BlockLabel>
              <SuppressHint>You pick the list next, and see who it would take out before saving.</SuppressHint>
              <SuppressOptions>
                {options.map(option => (
                  <SuppressBtn
                    key={option.type}
                    type="button"
                    title={option.type === 'profile' ? option.value : undefined}
                    onClick={() => onSuppress(option.type, option.value)}
                  >
                    {option.label}
                  </SuppressBtn>
                ))}
              </SuppressOptions>
            </SuppressBlock>
          );
        })()}

        <Sep />

        {/* ── Activity ─────────────────────────────────── */}
        <BlockLabel>Activity</BlockLabel>
        <MetaGrid>
          <MetaKey>Status</MetaKey>
          <MetaVal><StatusPill $color={statusMeta.color} $bg={statusMeta.bg}>{statusMeta.label}</StatusPill></MetaVal>
          <MetaKey>Source</MetaKey>
          <MetaVal>{SOURCE_LABEL[lead.source] ?? lead.source}</MetaVal>
          <MetaKey>Enrolled</MetaKey>
          <MetaVal>{fmt(lead.enrolledAt)}</MetaVal>
          {lead.lastContactedAt && <><MetaKey>Connected</MetaKey><MetaVal>{fmt(lead.lastContactedAt)}</MetaVal></>}
          {lead.messageSentAt   && <><MetaKey>Msg sent</MetaKey><MetaVal>{fmt(lead.messageSentAt)}</MetaVal></>}
          {isProxy && acctId && (
            <>
              <MetaKey>Sent via</MetaKey>
              <MetaVal>
                <AccountChip $missing={acctMissing} title={acctId}>
                  <SvgFragment type="linkedin" />
                  {acctName}
                </AccountChip>
                {acctMissing && <AccountWarn>account disconnected — steps will fail until reassigned</AccountWarn>}
              </MetaVal>
            </>
          )}
          {lead.status === 'failed' && lead.failureReason && (
            <><MetaKey>Fail reason</MetaKey><MetaVal>{formatFailureReason(lead.failureReason)}</MetaVal></>
          )}
          {isExcludedAsConnected(lead) && (
            <>
              <MetaKey>Skipped</MetaKey>
              <MetaVal>
                Already connected with you
                {lead.suppression?.at && <MsgTime> · {fmt(lead.suppression.at)}</MsgTime>}
              </MetaVal>
            </>
          )}
          {isSuppressed && !isExcludedAsConnected(lead) && (
            <>
              <MetaKey>Suppressed by</MetaKey>
              <MetaVal>
                {describeLeadSuppression(lead)}
                {lead.suppression?.listName && <> on “{lead.suppression.listName}”</>}
                {lead.suppression?.at && <MsgTime> · {fmt(lead.suppression.at)}</MsgTime>}
              </MetaVal>
              {lead.suppression?.note && (
                <><MetaKey>Reason</MetaKey><MetaVal>{lead.suppression.note}</MetaVal></>
              )}
            </>
          )}
          {lead.nextStepAt && !TERMINAL_LEAD_STATUSES.includes(lead.status) && (
            <><MetaKey>Next step</MetaKey><MetaVal><NextStepTime>{fmtTime(lead.nextStepAt)}</NextStepTime></MetaVal></>
          )}
        </MetaGrid>

        <Sep />

        {/* ── Sequence progress ────────────────────────── */}
        <BlockLabel>Sequence progress</BlockLabel>
        <VerticalStepper
          currentStep={isPositiveTerminal ? sequence.length : (isFailed ? sequence.length : stepIdx)}
          completedSteps={
            isPositiveTerminal
              ? sequence.map((_, i) => i)
              : Array.from({ length: stepIdx }, (_, i) => i)
          }
          steps={[
            ...sequence.map((step): IVerticalStep => {
              const meta = STEP_META[step.type];
              return {
                label: meta.label,
                icon: <StepSvgWrap $color={meta.color} $bg={meta.bg}><SvgFragment type={meta.icon} /></StepSvgWrap>,
                sublabel: formatStepDelay(step) ? `After ${formatStepDelay(step)}` : undefined,
              };
            }),
            { label: 'Outcome', sublabel: hasMessageStep
              ? 'Replied (auto) or marked Converted / Closed Lost / Failed'
              : 'Marked Converted, Closed Lost, or Failed',
              icon: <StepSvgWrap $color="#6366f1" $bg="#eef2ff"><SvgFragment type="stepGoal" /></StepSvgWrap> },
          ]}
          accentColor={styles.colors.primary600}
          size="sm"
        />

        {/* ── Messages & activity ─────────────────────── */}
        <Sep />
        <BlockLabel>Messages &amp; activity</BlockLabel>
        {(() => {
          const messages = lead.messages ?? [];
          const showOutcome = TERMINAL_LEAD_STATUSES.includes(lead.status);
          const outcomeMeta = OUTCOME_ACTIVITY[lead.status];
          const outcomeAt = outcomeActivityTime(lead);

          if (messages.length === 0 && !showOutcome) {
            return (
              <EmptyHint>
                {lead.status === 'pending'
                  ? 'No activity yet — lead is pending.'
                  : 'No messages recorded yet.'}
              </EmptyHint>
            );
          }

          return (
            <MessageList>
              {messages.map((msg, i) => {
                if (msg.stepType === 'retry' || msg.stepType === 'status_change' || msg.direction === 'system') {
                  const icon = msg.stepType === 'retry' ? 'refresh' : 'stepGoal';
                  return (
                    <ActivityItem key={i}>
                      <ActivityDot $color={styles.colors.primary600} $bg={styles.colors.primary100}>
                        <SvgFragment type={icon} />
                      </ActivityDot>
                      <ActivityText>
                        {msg.content}
                        {msg.sentAt && <MsgTime> · {fmtTime(msg.sentAt)}</MsgTime>}
                      </ActivityText>
                    </ActivityItem>
                  );
                }

                const isOut    = msg.direction === 'outbound';
                const meta     = STEP_META[msg.stepType as SequenceStepType];

                if (msg.stepType === 'post_comment') {
                  return (
                    <PostCommentBlock key={i}>
                      <ActivityItem>
                        <ActivityDot $color={meta?.color ?? MUTED} $bg={meta?.bg ?? '#f1f5f9'}>
                          {meta ? <SvgFragment type={meta.icon} /> : '•'}
                        </ActivityDot>
                        <ActivityText>
                          {renderThePostActivity('Commented', msg.postUrl)}
                          {msg.aiGenerated && <AiBadge>✦ AI</AiBadge>}
                          {msg.sentAt && <MsgTime> · {fmtTime(msg.sentAt)}</MsgTime>}
                        </ActivityText>
                      </ActivityItem>
                      <CommentBubbleWrap $outbound={isOut}>
                        <Bubble $outbound={isOut}>{msg.content}</Bubble>
                      </CommentBubbleWrap>
                    </PostCommentBlock>
                  );
                }

                const isActivity = msg.stepType === 'connection_request' || msg.stepType === 'post_like' || msg.stepType === 'profile_visit';
                return isActivity ? (
                  <ActivityItem key={i}>
                    <ActivityDot $color={meta?.color ?? MUTED} $bg={meta?.bg ?? '#f1f5f9'}>
                      {meta ? <SvgFragment type={meta.icon} /> : '•'}
                    </ActivityDot>
                    <ActivityText>
                      {msg.stepType === 'post_like'
                        ? renderThePostActivity('Liked', msg.postUrl)
                        : msg.content}
                      {msg.sentAt && <MsgTime> · {fmtTime(msg.sentAt)}</MsgTime>}
                    </ActivityText>
                  </ActivityItem>
                ) : (
                  <MsgGroup key={i} $outbound={isOut}>
                    <MsgLabel $outbound={isOut}>
                      {isOut ? (
                        <>
                          {meta && <MsgIcon $color={meta.color}><SvgFragment type={meta.icon} /></MsgIcon>}
                          {meta?.label ?? 'Message'}
                        </>
                      ) : '↩ Reply'}
                      {msg.aiGenerated && <AiBadge>✦ AI</AiBadge>}
                      {msg.sentAt && <MsgTime>{fmtTime(msg.sentAt)}</MsgTime>}
                    </MsgLabel>
                    <Bubble $outbound={isOut}>{msg.content}</Bubble>
                  </MsgGroup>
                );
              })}
              {showOutcome && outcomeMeta && (
                <ActivityItem key="outcome">
                  <ActivityDot $color={outcomeMeta.color} $bg={outcomeMeta.bg}>
                    <SvgFragment type={lead.status === 'failed' ? 'error' : 'stepGoal'} />
                  </ActivityDot>
                  <ActivityText>
                    {outcomeActivityText(lead)}
                    {outcomeAt && <MsgTime> · {fmtTime(outcomeAt)}</MsgTime>}
                  </ActivityText>
                </ActivityItem>
              )}
            </MessageList>
          );
        })()}
      </Body>
    </Wrap>
  );
};

// ─── Styled components ────────────────────────────────────────────────────────

const ACCENT  = styles.colors.primary600;
const BORDER  = '#e2e8f0';
const INK     = '#1e293b';
const MUTED   = '#94a3b8';
const SUBTLE  = '#f8fafc';

export const SIDEBAR_WIDTH = 360;

const Wrap = styled.div`
  width: ${SIDEBAR_WIDTH}px; flex-shrink: 0; display: flex; flex-direction: column;
  border-left: 1px solid ${BORDER}; background: #fff; height: 100%; overflow: hidden;
  ${campaignMobile`
    width: 100%;
    border-left: none;
    border-radius: 16px 16px 0 0;
    max-height: 92vh;
  `}
`;

const TopBar = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  padding: 12px 16px; border-bottom: 1px solid ${BORDER}; flex-shrink: 0; background: ${SUBTLE};
`;

const TopTitle = styled.span`
  font-family: NunitoSans; font-size: 10px; font-weight: 800;
  text-transform: uppercase; letter-spacing: 0.09em; color: ${MUTED};
`;

const CloseBtn = styled.button`
  width: 24px; height: 24px; border: none; background: transparent; border-radius: 5px;
  cursor: pointer; display: flex; align-items: center; justify-content: center;
  .svg { width: 13px; height: 13px; } .path { fill: ${MUTED} !important; }
  &:hover { background: #f1f5f9; }
`;

const Body = styled.div`
  flex: 1; overflow-y: auto; overflow-x: hidden; padding: 16px 16px 28px;
  display: flex; flex-direction: column; gap: 14px;
  scrollbar-width: thin; scrollbar-color: ${BORDER} transparent;
`;

const Sep = styled.div`height: 1px; background: ${BORDER}; flex-shrink: 0;`;

const BlockLabel = styled.div`
  font-family: NunitoSans; font-size: 10px; font-weight: 800;
  text-transform: uppercase; letter-spacing: 0.09em; color: ${MUTED};
`;

// Profile
const ProfileBlock = styled.div`
  display: flex; gap: 14px; align-items: flex-start;
  background: ${SUBTLE}; border: 1px solid ${BORDER}; border-radius: 12px; padding: 14px;
`;

const AvatarWrap = styled.div<{ $hasImage: boolean }>`
  width: 54px; height: 54px; border-radius: 50%; flex-shrink: 0; overflow: hidden;
  background: ${({ $hasImage }) => $hasImage ? 'transparent' : '#eef2ff'};
  color: ${ACCENT}; font-family: NunitoSans; font-weight: 800; font-size: 18px;
  display: flex; align-items: center; justify-content: center;
  border: 2px solid ${({ $hasImage }) => $hasImage ? 'transparent' : '#c7d2fe'};
  box-shadow: 0 1px 4px rgba(0,0,0,0.08);
`;

const ProfileName = styled.div`
  font-family: NunitoSans; font-size: 14px; font-weight: 800; color: ${INK}; line-height: 1.3;
`;

const ProfileSub = styled.div<{ $bold?: boolean }>`
  font-family: NunitoSans; font-size: 11.5px; color: #64748b;
  font-weight: ${({ $bold }) => $bold ? 700 : 400}; line-height: 1.4;
`;

const LinkedInBtn = styled.a`
  display: inline-flex; align-items: center; gap: 7px;
  padding: 7px 14px; border-radius: 8px; text-decoration: none; align-self: flex-start;
  font-family: NunitoSans; font-size: 11px; font-weight: 700;
  color: ${ACCENT}; border: 1px solid ${BORDER}; background: ${SUBTLE};
  .svg { width: 13px; height: 13px; flex-shrink: 0; }
  .path { fill: ${ACCENT} !important; }
  &:hover { background: #eef2ff; border-color: #c7d2fe; opacity: 1; }
`;

// Suppression
const SuppressBlock = styled.div`display: flex; flex-direction: column; gap: 6px;`;

const SuppressHint = styled.div`
  font-family: NunitoSans; font-size: 11px; color: #64748b; line-height: 1.4;
`;

const SuppressOptions = styled.div`display: flex; flex-wrap: wrap; gap: 6px;`;

const SuppressBtn = styled.button`
  max-width: 100%; padding: 6px 12px; border-radius: 8px; cursor: pointer;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  font-family: NunitoSans; font-size: 11px; font-weight: 700;
  color: #b4504c; border: 1px solid ${BORDER}; background: ${SUBTLE};
  &:hover:not(:disabled) { background: #fbecec; border-color: #f3c4c2; }
  &:disabled { cursor: default; opacity: 0.6; }
`;

// Meta grid
const MetaGrid = styled.div`display: grid; grid-template-columns: auto 1fr; gap: 6px 12px; align-items: center;`;
const MetaKey  = styled.span`font-family: NunitoSans; font-size: 10px; font-weight: 700; color: ${MUTED}; white-space: nowrap; text-transform: uppercase; letter-spacing: 0.05em;`;
const MetaVal  = styled.span`font-family: NunitoSans; font-size: 11px; color: #475569;`;

const StatusPill = styled.span<{ $color: string; $bg: string }>`
  display: inline-block; padding: 2px 9px; border-radius: 6px;
  font-family: NunitoSans; font-size: 10px; font-weight: 800;
  color: ${({ $color }) => $color}; background: ${({ $bg }) => $bg};
`;

const AccountChip = styled.span<{ $missing: boolean }>`
  display: inline-flex; align-items: center; gap: 5px; max-width: 100%;
  padding: 2px 8px; border-radius: 6px;
  font-family: NunitoSans; font-size: 10.5px; font-weight: 700;
  color: ${({ $missing }) => $missing ? '#dc2626' : ACCENT};
  background: ${({ $missing }) => $missing ? '#fee2e2' : '#eef2ff'};
  border: 1px solid ${({ $missing }) => $missing ? '#fecaca' : '#c7d2fe'};
  .svg { width: 11px; height: 11px; flex-shrink: 0; }
  .path { fill: ${({ $missing }) => $missing ? '#dc2626' : ACCENT} !important; }
`;

const AccountWarn = styled.div`
  font-family: NunitoSans; font-size: 9.5px; font-weight: 600; color: #dc2626;
  margin-top: 3px; line-height: 1.3;
`;

// Messages
const MessageList = styled.div`display: flex; flex-direction: column; gap: 8px;`;

const PostCommentBlock = styled.div`display: flex; flex-direction: column; gap: 4px;`;

const CommentBubbleWrap = styled.div<{ $outbound: boolean }>`
  display: flex;
  justify-content: ${({ $outbound }) => $outbound ? 'flex-end' : 'flex-start'};
  padding-left: 32px;
`;

const MsgGroup = styled.div<{ $outbound: boolean }>`
  display: flex; flex-direction: column;
  align-items: ${({ $outbound }) => $outbound ? 'flex-end' : 'flex-start'}; gap: 3px;
`;

const MsgLabel = styled.div<{ $outbound: boolean }>`
  display: flex; align-items: center; gap: 6px;
  flex-direction: ${({ $outbound }) => $outbound ? 'row-reverse' : 'row'};
  font-family: NunitoSans; font-size: 10px; font-weight: 700; color: ${MUTED};
`;

const MsgTime = styled.span`font-weight: 400; color: #c4cdd8;`;

const Bubble = styled.div<{ $outbound: boolean }>`
  max-width: 90%; padding: 8px 12px; word-break: break-word; white-space: pre-wrap;
  font-family: NunitoSans; font-size: 12px; line-height: 1.6; border-radius: 10px;
  ${({ $outbound }) => $outbound
    ? `border-bottom-right-radius: 2px; background: ${ACCENT}; color: #fff;`
    : `border-bottom-left-radius: 2px; background: #f1f5f9; color: ${INK};`}
`;

const EmptyHint = styled.div`
  font-family: NunitoSans; font-size: 12px; color: ${MUTED};
  padding: 4px 0;
`;

const LocationIcon = styled.span`
  display: inline-flex; align-items: center; margin-right: 3px; vertical-align: middle;
  .svg { width: 11px; height: 11px; } .path { stroke: #94a3b8; fill: none; }
`;

const StepSvgWrap = styled.span<{ $color: string; $bg: string }>`
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
  width: 20px; height: 20px; border-radius: 6px;
  background: ${({ $bg }) => $bg};
  .svg { width: 11px; height: 11px; }
  .path { stroke: ${({ $color }) => $color}; fill: none; }
`;

const ActivityItem = styled.div`
  display: flex; align-items: center; gap: 8px; padding: 4px 0;
`;

const ActivityDot = styled.span<{ $color: string; $bg: string }>`
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
  width: 24px; height: 24px; border-radius: 7px; background: ${({ $bg }) => $bg};
  .svg { width: 12px; height: 12px; }
  .path { stroke: ${({ $color }) => $color}; fill: none; }
`;

const MsgIcon = styled.span<{ $color: string }>`
  display: flex; align-items: center; flex-shrink: 0;
  .svg { width: 11px; height: 11px; }
  .path { stroke: ${({ $color }) => $color}; fill: none; }
`;

const ActivityText = styled.span`
  font-family: NunitoSans; font-size: 12px; color: #475569;
`;

const ActivityPostLink = styled.a`
  color: ${ACCENT};
  font-weight: 700;
  text-decoration: none;
  &:hover { text-decoration: underline; }
`;

const NextStepTime = styled.span`
  color: ${ACCENT}; font-weight: 700;
`;

const AiBadge = styled.span`
  padding: 1px 7px; border-radius: 4px; font-size: 9px; font-weight: 800;
  background: #f0f4ff; color: ${ACCENT}; letter-spacing: 0.06em;
  text-transform: uppercase; white-space: nowrap;
`;

export default CampaignLeadSidebar;
